'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { isAdminRole } from '@/lib/supabaseService';

/**
 * Landing point for clients arriving from the public website.
 *
 * After registration the website redirects here with the freshly created
 * Supabase session in the URL fragment:
 *
 *   /login#access_token=…&refresh_token=…&type=signup
 *
 * This page adopts that session so the client goes straight into the dashboard.
 * It previously redirected to "/" immediately, which discarded the fragment and
 * forced every new signup to type their password again.
 *
 * With no fragment it falls back to the login form at "/", forwarding any
 * ?email= hint so the field can be prefilled.
 */
export default function LoginHandoff() {
  const router = useRouter();
  const [status, setStatus] = useState<'working' | 'failed'>('working');

  useEffect(() => {
    const supabase = createClient();

    const run = async () => {
      const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      // No handoff — hand over to the real login form.
      if (!accessToken || !refreshToken) {
        const email = new URLSearchParams(window.location.search).get('email');
        router.replace(email ? `/?email=${encodeURIComponent(email)}` : '/');
        return;
      }

      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      // Strip the tokens from the address bar either way — they should not sit
      // in history or get copied out of the URL.
      window.history.replaceState(null, '', window.location.pathname);

      if (error || !data.session) {
        setStatus('failed');
        setTimeout(() => router.replace('/'), 2500);
        return;
      }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role, is_suspended')
        .eq('id', data.session.user.id)
        .maybeSingle();

      if (profile?.is_suspended) {
        await supabase.auth.signOut();
        setStatus('failed');
        setTimeout(() => router.replace('/'), 2500);
        return;
      }

      // Full navigation so the new session is definitely readable on arrival.
      window.location.assign(isAdminRole(profile?.role) ? '/admin-panel' : '/client-dashboard');
    };

    run().catch(() => {
      setStatus('failed');
      setTimeout(() => router.replace('/'), 2500);
    });
  }, [router]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      {status === 'working' ? (
        <>
          <div className="animate-spin w-8 h-8 rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        </>
      ) : (
        <>
          <p className="text-sm font-semibold text-foreground">
            We could not complete your sign-in automatically.
          </p>
          <p className="text-sm text-muted-foreground">
            Taking you to the login page — please sign in with your email and password.
          </p>
        </>
      )}
    </div>
  );
}
